import { playableItemEvents } from "../../events";
import PlayableItem, { ItemProps } from "./item";
import bus from "../../../bus";
import { playerEvents } from "../../events";
import { Platform } from "../../../platforms";

type StreamProps = ItemProps & {
  stream: Platform,
  listenSessionID: string,
  volume: number,
  status?: "playing" | "paused" | "stopped",
};

export default class PlayableStream extends PlayableItem<StreamProps> {
  props: StreamProps = (undefined as any); // always set in constructor
  prevProps?: StreamProps;

  audio: HTMLAudioElement | null = null;
  ready = true;

  constructor(props: StreamProps) {
    super(props);

    // Bind event handlers to instance
    this.onCreate = this.onCreate.bind(this);
    this.onDestroy = this.onDestroy.bind(this);
    this.load = this.load.bind(this);

    this.set(props);
  }

  set(props: StreamProps) {
    console.trace("set", props);
    this.prevProps = this.props || {};
    this.props = props;
    this.onUpdate();
  }

  isReady() {
    return this.ready;
  }

  onUpdate() {
    const { status } = this.props;

    if (status !== this.prevProps?.status) {
      switch (status) {
        case "playing":
          this.play();
          break;
        case "paused":
          this.pause();
          break;
        case "stopped":
          this.stop();
          break;
        default:
          break;
      }
    }

    this.setAudioVolume();
  }

  play() {
    this.stop();

    // create new audio:
    this.audio = new Audio();
    this.audio.addEventListener("play", () => this.onPlay());
    this.audio.addEventListener("playing", () => this.onPlaying());
    this.audio.addEventListener("stalled", () => this.onStalled());
    this.audio.addEventListener("pause", () => this.onPause());
    this.audio.addEventListener("ended", () => this.onEnded());
    this.audio.addEventListener("error", () => this.onError());

    // set props
    this.ready = false;
    this.setAudioVolume();
    this.audio.src = this.props.stream.stream_url;
    this.audio.load();
    this.audio.play();
  }

  get volume() {
    return isFinite(this.props.volume) ? this.props.volume : 1;
  }

  private setAudioVolume() {
    if (this.audio) {
      const volume = this.volume;
      this.audio.volume = volume;
      this.audio.muted = (volume === 0);
    }
  }

  stop() {
    if (this.audio) {
      this.audio.src = "";
      this.audio.load();
    }
    this.audio = null;
    this.ready = true;
  }

  pause() {
    this.stop();
  }

  load() {
  }

  onCreate() {
  }

  onDestroy() {
    super.onDestroy();
    this.stop();
  }

  hasDuration() {
    return false;
  }

  onReady() {
    const { status } = this.props;
    switch (status) {
      case "playing":
        this.play();
        break;
      case "paused":
        this.pause();
        break;
      case "stopped":
        this.stop();
        break;
      default:
        break;
    }
  }

  onPlay() {
    this.emit(playableItemEvents.STATUS_CHANGE, { status: "playing" });
  }

  onPlaying() {
    this.ready = true;
    this.emit(playableItemEvents.STATUS_CHANGE, { status: "playing" });
  }

  onStalled() {
    this.ready = false;
  }

  onPause() {
    this.emit(playableItemEvents.STATUS_CHANGE, { status: "paused" });
  }

  onStop() {
    this.emit(playableItemEvents.STATUS_CHANGE, { status: "stopped" });
  }

  onEnded() {
    this.emit(playableItemEvents.STATUS_CHANGE, { status: "ended" });
    this.ready = false;
  }

  onError() {
    bus.emit(playerEvents.PAUSE);
  }

  isIphone() {
    return (
      /CriOS/i.test(window.navigator.userAgent) ||
      /FxiOS/i.test(window.navigator.userAgent) ||
      /iPhone/.test(window.navigator.userAgent)
    );
  }

  getPlayerItemID() {
    const { stream } = this.props;
    return stream.id;
  }

  // TODO - when PlayableItem is TS remove
  // @ts-ignore-next-line
  getTitle() {
    const { stream } = this.props;

    if (stream.current_item_name) {
      return stream.current_item_name;
    } else {
      return stream.name;
    }
  }

  getDuration() {
    return Infinity;
  }

  getCurrentTime() {
    return 0;
  }

  isLive() {
    return true;
  }

  // TODO - when PlayableItem is TS remove
  // @ts-ignore-next-line
  key() {
    const { stream, listenSessionID } = this.props;
    // FIXME The `key` means we only create new vnodes when we get new data, but
    // like we should really do that ourselves so we don’t have to rely on the
    // hyperapp lifecycle methods
    return this.props ? `stream__${stream.id}__${listenSessionID}` : "";
  }
}
